import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Play, LogIn, LogOut, Upload, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="backdrop-blur-xl bg-background/80 border-b border-border/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="header-logo"
          >
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Play className="w-5 h-5 text-primary-foreground fill-current" />
            </div>
            <span className="font-serif text-xl md:text-2xl font-semibold text-foreground tracking-tight">
              Tower Lakes Chapel
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-media-library"
            >
              Media Library
            </Link>
            <Link 
              to="/?category=sunday-service" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-sunday-service"
            >
              Sunday Service
            </Link>
            <Link 
              to="/?category=bible-study" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-bible-study"
            >
              Bible Study
            </Link>
            
            {user && user.role === 'admin' ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    data-testid="admin-menu-trigger"
                  >
                    Admin
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-dashboard-link">
                    <Upload className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="default" 
                size="sm"
                onClick={() => navigate('/login')}
                data-testid="login-button"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Admin Login
              </Button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border animate-slide-in">
            <nav className="flex flex-col gap-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-home"
              >
                <Home className="w-4 h-4" />
                Media Library
              </Link>
              <Link 
                to="/?category=sunday-service" 
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sunday Service
              </Link>
              <Link 
                to="/?category=bible-study" 
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Bible Study
              </Link>
              
              {user && user.role === 'admin' ? (
                <>
                  <Link 
                    to="/admin" 
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-admin"
                  >
                    <Upload className="w-4 h-4" />
                    Admin Dashboard
                  </Link>
                  <button 
                    className="flex items-center gap-2 text-sm font-medium text-destructive hover:text-destructive/80"
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    data-testid="mobile-logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-login-link"
                >
                  <LogIn className="w-4 h-4" />
                  Admin Login
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
